import { Checkbox } from "@comps/ui/checkbox";
import { Label } from "@comps/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { AllRightOptions, isOptionAllowed, OPTION_MIN_LEVEL, RightAccess, RightOption } from "@dto/Users";
import { TW_RoleDetailsRightOptionLabel, TW_RoleDetailsRightOptionLabelDisabled, TW_RoleDetailsRightOptionRow, TW_RoleDetailsRightOptionsMenu, TW_RoleDetailsRightOptionsMenuTitle, TW_RoleDetailsRightOptionsTrigger } from "@utils/twClasses";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";

interface RightOptionsMenuProps {
  /** Unique prefix for the checkbox ids (several menus live on the same page). */
  idPrefix: string;
  /** Access level of the right: an option the level does not allow is disabled. */
  level: RightAccess;
  options: RightOption[];
  onChange: (options: RightOption[]) => void;
  disabled?: boolean;
}

/**
 * "Options…" menu of a right in the role editor: one checkbox per option
 * (add, delete, export records). An option that the level of the right does
 * not allow (see OPTION_MIN_LEVEL) is disabled, unchecked, and explains the
 * minimum level in a tooltip.
 */
function RightOptionsMenu({ idPrefix, level, options, onChange, disabled = false }: RightOptionsMenuProps) {
  const { t } = useTranslation();
  const wsColor = useModelStore(state => state.wsColor);

  const toggle = (option: RightOption, checked: boolean) => {
    const next = checked ? [...options, option] : options.filter(o => o !== option);
    // Canonical order, no duplicate
    onChange(AllRightOptions.filter(o => next.includes(o)));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={TW_RoleDetailsRightOptionsTrigger} disabled={disabled}>
          {t("right.option.menu.trigger")}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className={TW_RoleDetailsRightOptionsMenu}>
        <span className={TW_RoleDetailsRightOptionsMenuTitle}>{t("right.option.menu.title")}</span>
        {AllRightOptions.map(option => {
          const allowed = isOptionAllowed(option, level);
          const checked = allowed && options.includes(option);
          const id = `${idPrefix}-option-${option}`;
          const row = (
            <div className={TW_RoleDetailsRightOptionRow}>
              <Checkbox
                id={id}
                className="h-3.5 w-3.5 [&_svg]:h-3 [&_svg]:w-3"
                checked={checked}
                disabled={!allowed}
                onCheckedChange={(value) => toggle(option, value === true)}
                style={checked ? { backgroundColor: wsColor, borderColor: wsColor } : {}}
              />
              <Label htmlFor={id} className={allowed ? TW_RoleDetailsRightOptionLabel : TW_RoleDetailsRightOptionLabelDisabled}>
                {t(`right.option.menu.${option}`)}
              </Label>
            </div>
          );
          if (allowed) {
            return <div key={option}>{row}</div>;
          }
          return (
            <TooltipProvider key={option}>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>{row}</TooltipTrigger>
                <TooltipContent>
                  {t("right.option.menu.disabled.tooltip", { p0: t(`right.access.value.${OPTION_MIN_LEVEL[option]}`) })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export default RightOptionsMenu;
